// ---------- ads ----------
// A banner pinned to the bottom of the screen, under the palette. It is a NATIVE view laid
// over the WebView, not an element of the page: the layout can't flow around it, so we
// publish its measured height as --ad-h and let .app reserve exactly that much padding.
// Nothing runs on the web build — a website uses AdSense, and putting AdSense inside an app
// WebView breaks the program rules, so the two surfaces stay separate.

import {
  AdMob,
  BannerAdPluginEvents,
  BannerAdPosition,
  BannerAdSize,
  RewardAdPluginEvents,
  type AdMobBannerSize,
} from '@capacitor-community/admob';

import { breakAdDue, pastGrace, rearm, resetPlayClock } from './adPacing';
import { consentGate, ensureConsent } from './consent';
import { isNative } from './platform';
import { installedAt } from './review';

// Google's public test unit: serves a placeholder ad with no AdMob account, no registered
// app and no store listing. The real unit arrives at build time, like the API URL.
const TEST_BANNER = 'ca-app-pub-3940256099942544/6300978111';
// `||` and not `??`: a build that passes the variable EMPTY — which is what sync-android.sh does
// while the real unit is still unknown — must fall back to the test unit like an absent one. With
// `??` the empty string wins, `showBanner` is called with no adId at all, and the failure reads as
// "no ad available" rather than as a missing configuration.
const BANNER_ID = (import.meta.env.VITE_ADMOB_BANNER_ID as string | undefined) || TEST_BANNER;

// Même unité de démonstration Google que la bannière, pour le format interstitiel.
const TEST_INTERSTITIAL = 'ca-app-pub-3940256099942544/1033173712';
const INTERSTITIAL_ID =
  (import.meta.env.VITE_ADMOB_INTERSTITIAL_ID as string | undefined) || TEST_INTERSTITIAL;

// Même logique, pour la pub récompensée du déblocage de pack (cf. src/packs.ts).
const TEST_REWARD = 'ca-app-pub-3940256099942544/5224354917';
const REWARD_ID = (import.meta.env.VITE_ADMOB_REWARD_ID as string | undefined) || TEST_REWARD;
// Et pour la création d'un puzzle au-delà des trois offertes (cf. src/creer.ts) : une unité à elle,
// la même unité de démonstration tant qu'aucune n'est passée au build.
const CREATE_REWARD_ID =
  (import.meta.env.VITE_ADMOB_CREATE_REWARD_ID as string | undefined) || TEST_REWARD;

/** Les deux gestes que paie une pub récompensée, chacun sur son unité AdMob. */
export type RewardUnit = 'pack' | 'create';
const REWARD_IDS: Record<RewardUnit, string> = { pack: REWARD_ID, create: CREATE_REWARD_ID };

// Le rappel de mise en page confié par main.ts, gardé au niveau du module : la bannière ne
// démarre plus forcément pendant initAds() — un lancement sans réseau la fait démarrer bien plus
// tard, cf. startBanner() — et il n'y a alors plus personne pour le repasser en paramètre.
let onResizeCb: () => void = () => {};

/** Height given back to the sheet when there is no ad on screen. */
function releaseSpace(): void {
  document.documentElement.style.setProperty('--ad-h', '0px');
  onResizeCb();
}

// A miss right at cold start — no network yet, or simply no fill in that instant — is very often
// transient, and giving up on the banner for the rest of the session over it was silencing ads for
// players who did nothing wrong. Retry with backoff instead, a few times: enough to ride out a slow
// network without turning into the kind of rapid-fire reloading AdMob's policies frown on.
const RETRY_DELAYS_MS = [30_000, 90_000, 240_000];

/** Le SDK est-il debout ? `AdMob.initialize()` ne doit tourner qu'une fois, mais plus d'un format
 *  a besoin de savoir qu'il a tourné. */
let adsInitialized = false;

/**
 * Le préalable commun aux trois formats : un consentement qui autorise la requête, puis un SDK
 * initialisé. Rendu FAUX plutôt que jeté — aucun appelant n'a de rattrapage à tenter, ils ont
 * tous la même conduite à tenir (rendre la place, ou renoncer à la pub).
 *
 * C'est ici que se règle la panne « aucune pub de toute la session » : `ensureConsent()` repose
 * la question à UMP quand le tour du démarrage n'a jamais abouti, au lieu de garder le verrou
 * fermé jusqu'au prochain lancement (cf. src/consent.ts).
 */
async function ensureAdsReady(): Promise<boolean> {
  if (!isNative()) return false;
  if (!(await ensureConsent()).ads) return false;
  if (adsInitialized) return true;
  try {
    await AdMob.initialize();
    adsInitialized = true;
    return true;
  } catch {
    // A missing APPLICATION_ID in AndroidManifest.xml throws right here. The game has to
    // stay playable without ads — never let this take the app down.
    return false;
  }
}

/** Une seule bannière par session : les écouteurs ci-dessous ne se posent qu'une fois. */
let bannerStarted = false;

/** Démarre la bannière, une fois pour la session. Appelée par initAds() au lancement, et en
 *  rattrapage par prepareRewardedAd() quand le consentement n'a été obtenu qu'après coup. */
async function startBanner(): Promise<void> {
  if (bannerStarted) return;
  bannerStarted = true;

  const requestBanner = () =>
    AdMob.showBanner({
      adId: BANNER_ID,
      adSize: BannerAdSize.ADAPTIVE_BANNER,
      position: BannerAdPosition.BOTTOM_CENTER,
      // Test mode only while the test unit is in use: switching it on with a real unit
      // would keep serving fake ads and earn nothing.
      isTesting: BANNER_ID === TEST_BANNER,
      margin: 0,
    });

  // Reset by a banner that DOES load: a later, unrelated miss (a rotation with no fill, a
  // network blip mid-session) earns its own fresh set of retries rather than inheriting an
  // exhausted count from the cold-start attempt.
  let retriesLeft = RETRY_DELAYS_MS.length;

  // The height isn't known before the ad loads, and an adaptive banner changes it on
  // rotation: hardcoding 50px would either clip the palette or waste a row of sheet.
  await AdMob.addListener(BannerAdPluginEvents.SizeChanged, (size: AdMobBannerSize) => {
    // height 0 is how the plugin reports "no banner on screen" — treat it like a failure
    // rather than reserving a zero-height strip.
    if (!size.height) return releaseSpace();
    retriesLeft = RETRY_DELAYS_MS.length;
    document.documentElement.style.setProperty('--ad-h', `${size.height}px`);
    onResizeCb();
  });

  // No network, or no ad to fill the slot: give the space back instead of leaving a gap
  // where the player expects the last rows of the grid — and queue a retry, unless this
  // session has already spent its budget of them.
  await AdMob.addListener(BannerAdPluginEvents.FailedToLoad, () => {
    releaseSpace();
    if (retriesLeft <= 0) return;
    const delay = RETRY_DELAYS_MS[RETRY_DELAYS_MS.length - retriesLeft];
    retriesLeft--;
    setTimeout(() => void requestBanner().catch(() => releaseSpace()), delay);
  });

  await requestBanner().catch(() => releaseSpace());
}

export async function initAds(onResize: () => void): Promise<void> {
  if (!isNative()) return;
  onResizeCb = onResize;
  // Consent first, and it is a hard gate: in the EEA and the UK an ad may not even be REQUESTED
  // before the answer is in, so this comes ahead of initialize() — see ensureAdsReady(), which
  // keeps the two in that order. Everywhere the question doesn't arise, UMP answers NOT_REQUIRED
  // and this costs one round trip at startup.
  if (!(await ensureAdsReady())) return releaseSpace();
  await startBanner();
}

// ---------- banner vs. the soft keyboard ----------
// The banner is a native view laid OVER the WebView (see the header comment) — it has no idea a
// dialog is open underneath it, and the WebView has no idea it exists either. Opening the
// keyboard to type the pseudo (src/prefs.ts, the one text field in the game) resizes the window,
// and the banner — anchored to the bottom of that now-shrunk window — ends up re-anchored right
// where the input field scrolls to keep itself visible: a native view always painted on top of
// the page, blocking every tap meant for the field underneath it. Hiding the banner for the
// short time the field is focused, and bringing it back on blur, sidesteps the collision
// entirely instead of fighting Android's own resize/IME behaviour.

/** Appelé au focus du champ pseudo. Sans effet si aucune bannière n'est affichée (pas de
 *  consentement, aucune pub chargée) — `hideBanner()` n'a alors rien à cacher. */
export async function hideBannerForInput(): Promise<void> {
  if (!isNative()) return;
  try {
    await AdMob.hideBanner();
  } catch {
    /* rien à cacher */
  }
}

/** Appelé au blur du champ pseudo — symétrique de hideBannerForInput(). */
export async function resumeBannerAfterInput(): Promise<void> {
  if (!isNative()) return;
  try {
    await AdMob.resumeBanner();
  } catch {
    /* rien à réafficher */
  }
}

// ---------- interstitial: leaving a just-finished puzzle ----------
// (Le second moment de ce même format, la pub de pause, est plus bas — cf. prepareBreakAd.)
// One ad, at this moment: the player has just completed a puzzle and is heading back to the
// catalogue (cf. S.justFinished, set by finishGame() in ui.ts and consumed by openHome() in
// home.ts). Never on a puzzle finished earlier and simply reopened to look at again — that isn't
// a transition the player asked to leave, and an ad there would punish revisiting a picture
// rather than reward finishing one.

/** Vrai une fois `prepareInterstitial()` résolu avec une pub effectivement chargée. Sert de garde
 *  à `showInterstitialIfReady()` : la préparation n'est jamais rattrapée à la volée, mieux vaut
 *  laisser le joueur partir sans pub que le faire attendre le chargement au moment où il clique. */
let interstitialLoaded = false;

/**
 * Précharge l'interstitielle dès que la partie est gagnée (appelé par finishGame()), PAS au
 * moment de quitter la feuille : le chargement prend une seconde ou deux, et le joueur regarde
 * son dessin fini pendant ce temps-là plutôt que d'attendre devant un catalogue qui tarde.
 */
export async function prepareInterstitial(): Promise<void> {
  interstitialLoaded = false;
  if (!(await ensureAdsReady())) return;
  try {
    await AdMob.prepareInterstitial({
      adId: INTERSTITIAL_ID,
      isTesting: INTERSTITIAL_ID === TEST_INTERSTITIAL,
    });
    interstitialLoaded = true;
  } catch {
    // Pas de réseau, aucune pub à servir : le joueur part au catalogue sans interstitielle,
    // exactement comme la bannière donne sa place en cas d'échec.
  }
}

/**
 * Affiche l'interstitielle si elle a fini de charger, sinon ne fait rien — jamais de nouvelle
 * tentative ici, la fenêtre est celle du clic qui vient d'arriver. `interstitialLoaded` est
 * consommé qu'elle s'affiche ou non : une pub ratée ne doit pas réapparaître à la sortie
 * suivante d'un puzzle qu'on n'a pourtant pas fini entre-temps.
 *
 * Rend vrai si la pub a été montrée — c'est ce que la pub de pause a besoin de savoir.
 */
export async function showInterstitialIfReady(): Promise<boolean> {
  const ready = interstitialLoaded;
  interstitialLoaded = false;
  if (!ready) return false;
  try {
    await AdMob.showInterstitial();
    // Une pub effectivement montrée, et elle seule, remet à zéro le compteur de la pub de pause :
    // c'est ce qui garantit l'écart entre deux interstitielles, celle de fin comprise (cf.
    // src/adPacing.ts).
    resetPlayClock();
    return true;
  } catch {
    // Le joueur continue vers le catalogue comme si de rien n'était.
    return false;
  }
}

// ---------- interstitial: a pause after a long stretch of coloring (src/adPacing.ts) ----------
// La même interstitielle, à un second moment : le retour au catalogue d'une feuille EN COURS,
// après assez de coloriage actif. Les règles sont dans src/adPacing.ts ; ici, seulement le SDK.

/**
 * Précharge la pub de pause. Appelé par fillCell() au franchissement du seuil, pendant que le
 * joueur colorie encore. Rien pendant la période de grâce : une pub chargée qu'on ne montrera
 * jamais ne coûte rien au joueur, mais fait baisser le taux d'affichage de l'unité.
 *
 * Rien non plus sans un consentement DÉJÀ acquis — `consentGate()`, et surtout pas
 * ensureConsent() par prepareInterstitial() : quand le tour UMP du démarrage n'a pas abouti
 * (lancement hors réseau), ensureConsent() repose la question, et le formulaire de Google
 * surgissait alors par-dessus la feuille, au beau milieu d'un geste, cinq minutes après le début
 * de la séance. Le rattrapage du consentement a ses moments à lui (cf. prepareRewardedAd) ; ce
 * n'en est pas un.
 */
export async function prepareBreakAd(): Promise<void> {
  if (!consentGate().ads) return;
  if (!pastGrace(Date.now(), installedAt())) return;
  await prepareInterstitial();
}

/**
 * Affiche la pub de pause si elle est due et prête, en quittant la feuille (openHome). Due mais
 * pas prête — pas de réseau, aucune pub à servir, ou chargement encore en cours — le joueur part
 * sans pub, et la prochaine case posée en redemandera une (cf. rearm).
 *
 * SYNCHRONE, et rend vrai si une pub monte : openHome() doit le savoir tout de suite pour ne pas
 * enchaîner la demande d'avis par-dessus (cf. src/review.ts, qui s'interdit justement d'être la
 * sollicitation de trop).
 *
 * Le « vrai » est donc OPTIMISTE : si showInterstitial() échoue ensuite (pub expirée, SDK), le
 * joueur n'a rien vu mais la demande d'avis a été sautée pour ce retour. Assumé : l'échec est
 * rare, et il penche du bon côté — une invitation manquée, jamais une de trop. Rendre le compte
 * exact demanderait de retarder paint() derrière une promesse du SDK, pour rattraper un retour.
 */
export function showBreakAdIfDue(): boolean {
  if (!breakAdDue(Date.now(), installedAt())) return false;
  if (!interstitialLoaded) {
    rearm();
    return false;
  }
  void showInterstitialIfReady().then((shown) => {
    if (!shown) rearm();
  });
  return true;
}

// ---------- rewarded: unlocking a pack (src/packs.ts) ----------
// Only on native — the web build never shows an ad for this, packs unlock for free and
// instantly there instead (cf. the plan; src/ads.ts:1-6 is the reason it can't be otherwise).
//
// Unlike the interstitial, there's no earlier moment to prepare against — the player picks
// which pack to unlock, nothing is known in advance beyond "the Packs page just opened". So
// prepareRewardedAd() is called then, speculatively (src/packs.ts, openPacksPage()): the load
// latency is mostly hidden by the time it takes to read the list and tap one.

/**
 * Combien de temps on fait patienter le joueur qui a tapé avant que la pub soit prête. Assez pour
 * couvrir un chargement de vidéo sur réseau lent, trop court pour qu'un joueur croie l'app figée
 * — au-delà on lui rend la main, et le chargement continue en arrière-plan pour sa prochaine
 * tentative plutôt que d'être jeté.
 */
const REWARD_WAIT_MS = 10_000;

/** Ce qu'un déblocage peut donner, du point de vue de la pub — trois cas qui appellent trois
 *  phrases différentes côté packs.ts, là où un simple `false` les confondait tous. */
export type RewardOutcome =
  /** Vue jusqu'à la récompense : le pack peut être débloqué. */
  | 'rewarded'
  /** Affichée mais refermée avant la fin : rien à reprocher, rien à donner non plus. */
  | 'skipped'
  /** Jamais affichée : pas de consentement, pas de réseau, ou aucune pub à servir. */
  | 'unavailable';

/** Vrai quand une pub est chargée et pas encore consommée. */
let rewardReady = false;
/** Pour QUEL geste elle est chargée — ou se charge. Le plugin ne tient qu'une pub récompensée à
 *  la fois : en préparer une pour l'autre unité remplace celle-ci, d'où le contrôle de loadRewarded. */
let rewardUnit: RewardUnit = 'pack';
/** Le chargement en cours, s'il y en a un — partagé par tous les appelants pour qu'un tap
 *  pendant la préparation attende CE chargement au lieu d'en lancer un deuxième en parallèle. */
let rewardLoading: Promise<boolean> | null = null;

/**
 * Charge une pub récompensée, ou rend celle qui est déjà prête. Idempotent et sans exception :
 * un échec (pas de réseau, aucune pub à servir sur cette unité) rend `false` et laisse l'état à
 * zéro, si bien que la tentative suivante repart d'un chargement neuf.
 */
function loadRewarded(unit: RewardUnit = 'pack'): Promise<boolean> {
  if (rewardUnit === unit) {
    if (rewardReady) return Promise.resolve(true);
    if (rewardLoading) return rewardLoading;
  } else if (rewardLoading) {
    // Une pub de l'AUTRE geste est en route : on la laisse finir, puis on charge la nôtre à sa
    // place. Deux préparations simultanées se marcheraient dessus dans le plugin.
    return rewardLoading.then(() => loadRewarded(unit));
  }
  rewardUnit = unit;
  rewardReady = false;
  const adId = REWARD_IDS[unit];
  rewardLoading = (async () => {
    try {
      if (!(await ensureAdsReady())) return false;
      await AdMob.prepareRewardVideoAd({ adId, isTesting: adId === TEST_REWARD });
      rewardReady = true;
      return true;
    } catch {
      // `prepareRewardVideoAd` rejette sur onAdFailedToLoad : pas de réseau, ou aucune pub à
      // servir. showRewardedAd() le constatera et répondra 'unavailable'.
      return false;
    } finally {
      rewardLoading = null;
    }
  })();
  return rewardLoading;
}

/**
 * Précharge, spéculativement, à l'ouverture de la page Packs (src/packs.ts) — la latence de
 * chargement est alors couverte par le temps de lire la liste et d'en choisir une.
 *
 * En profite pour rattraper la bannière : si la session a démarré sans consentement obtenu, le
 * verrou vient peut-être de s'ouvrir à l'instant (cf. ensureConsent()), et il n'y a pas de raison
 * de laisser la bannière absente jusqu'au prochain lancement.
 */
export async function prepareRewardedAd(unit: RewardUnit = 'pack'): Promise<void> {
  if (!(await ensureAdsReady())) return;
  void startBanner();
  void loadRewarded(unit);
}

/**
 * Affiche la pub récompensée et résout une fois que le joueur l'a refermée.
 *
 * ATTEND le chargement si le joueur a tapé trop tôt, au lieu de renoncer : la page Packs déclenche
 * un déblocage au premier tap sur une card, sans confirmation, donc le tap arrive régulièrement
 * une seconde après l'ouverture — avant qu'une vidéo ait pu se charger. Renoncer là faisait
 * « je tape et il ne se passe rien », le symptôme même qui a lancé cette enquête.
 *
 * RECHARGE ensuite, systématiquement : une pub est consommée par son affichage, et sans ce
 * rechargement la deuxième tentative de la même visite — deuxième pack, ou simple nouvel essai —
 * ne trouvait plus rien et échouait en silence jusqu'à ce qu'on quitte et rouvre la page.
 *
 * `Dismissed` se déclenche dans les DEUX cas (fermée avant la fin, ou vue jusqu'au bout) —
 * documenté ainsi par le plugin lui-même sur RewardAdPluginEvents.Dismissed — donc c'est
 * `Rewarded` qui pose le drapeau, et `Dismissed` qui referme la promesse en le lisant. Les
 * écouteurs sont retirés dès que la promesse se résout : sans ça, tenter plusieurs pubs dans la
 * même session empilerait un jeu d'écouteurs par tentative.
 */
export async function showRewardedAd(unit: RewardUnit = 'pack'): Promise<RewardOutcome> {
  if (!(await waitForReward(unit))) return 'unavailable';
  // Consommée : le plugin garde son `mRewardedAd` en statique et ne le vide pas après
  // affichage, c'est donc à nous de ne jamais montrer deux fois la même.
  rewardReady = false;

  const rewarded = await new Promise<boolean>((resolve) => {
    let earned = false;
    let settled = false;
    const rewardedHandle = AdMob.addListener(RewardAdPluginEvents.Rewarded, () => {
      earned = true;
    });
    const dismissedHandle = AdMob.addListener(RewardAdPluginEvents.Dismissed, () => finish(earned));
    const failedHandle = AdMob.addListener(RewardAdPluginEvents.FailedToShow, () => finish(false));

    function finish(result: boolean): void {
      if (settled) return;
      settled = true;
      void Promise.all([rewardedHandle, dismissedHandle, failedHandle]).then((hs) =>
        hs.forEach((h) => h.remove()),
      );
      resolve(result);
    }

    void AdMob.showRewardVideoAd().catch(() => finish(false));
  });

  void loadRewarded(unit);
  return rewarded ? 'rewarded' : 'skipped';
}

/** Le chargement, borné dans le temps. Le dépassement rend la main au joueur SANS annuler le
 *  chargement : celui-ci finit sa course et servira la tentative suivante. */
function waitForReward(unit: RewardUnit): Promise<boolean> {
  const loading = loadRewarded(unit);
  return new Promise<boolean>((resolve) => {
    const timer = setTimeout(() => resolve(false), REWARD_WAIT_MS);
    void loading.then((ok) => {
      clearTimeout(timer);
      resolve(ok);
    });
  });
}
